import React, { useState, useEffect } from 'react'
import CashFlowForm from '../components/CashFlowForm';
import { formatDate as formatDateUtil } from '../utils/dateFormat';
import { apiGet, apiDelete } from '../utils/api';

interface CashFlow {
  id: string;
  amount: number;
  description: string;
  date: string;
  reference?: string;
  fromAccountId: string;
  toAccountId: string;
  fromAccount: {
    id: string;
    name: string;
    type: string;
  };
  toAccount: {
    id: string;
    name: string;
    type: string;
  };
  tournament?: {
    id: string;
    name: string;
  };
}


const CashFlow: React.FC = () => {
  const [cashFlows, setCashFlows] = useState<CashFlow[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCashFlow, setEditingCashFlow] = useState<CashFlow | null>(null);
  const [showJSONModal, setShowJSONModal] = useState(false);
  const [selectedCashFlow, setSelectedCashFlow] = useState<CashFlow | null>(null);
  const [filter, setFilter] = useState({
    fromAccountId: '',
    toAccountId: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchCashFlows();
  }, [filter]);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchCashFlows = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (filter.fromAccountId) params.fromAccountId = filter.fromAccountId;
      if (filter.toAccountId) params.toAccountId = filter.toAccountId;
      if (filter.startDate) params.startDate = filter.startDate;
      if (filter.endDate) params.endDate = filter.endDate;
      
      const data = await apiGet<{ cashFlows: CashFlow[] }>('/cashflow', params);
      setCashFlows(data.cashFlows || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };


  const fetchAccounts = async () => {
    try {
      const data = await apiGet<any[]>('/cash-accounts');
      setAccounts(data);
    } catch (err) {
      console.error('Ошибка загрузки счетов:', err);
    }
  };


  const handleCreate = () => {
    setEditingCashFlow(null);
    setShowForm(true);
  };

  const handleEdit = (cashFlow: CashFlow) => {
    setEditingCashFlow(cashFlow);
    setShowForm(true);
  };

  const handleFormSave = (cashFlow: CashFlow) => {
    setShowForm(false);
    setEditingCashFlow(null);
    fetchCashFlows();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCashFlow(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту операцию?')) {
      return;
    }

    try {
      await apiDelete(`/cashflow/${id}`);
      // Обновляем список операций
      fetchCashFlows();
    } catch (err: any) {
      setError(err.message || 'Ошибка при удалении');
    }
  };

  const handleViewJSON = (flow: CashFlow) => {
    setSelectedCashFlow(flow);
    setShowJSONModal(true);
  };

  const handleCloseJSONModal = () => {
    setShowJSONModal(false);
    setSelectedCashFlow(null);
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' MDL';
  };

  const formatDate = (dateString: string) => {
    return formatDateUtil(dateString);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Ошибка: {error}</p>
        <button 
          onClick={fetchCashFlows}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Попробовать снова
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Перемещение денежных средств</h1>
        <button 
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Добавить операцию
        </button>
      </div>

      {/* Фильтры */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Счет-отправитель
                </label>
                <select
                  value={filter.fromAccountId}
                  onChange={(e) => setFilter({...filter, fromAccountId: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Все счета</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.type})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Счет-получатель
                </label>
                <select
                  value={filter.toAccountId}
                  onChange={(e) => setFilter({...filter, toAccountId: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Все счета</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.type})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Дата с
                </label>
                <input
                  type="date"
                  value={filter.startDate}
                  onChange={(e) => setFilter({...filter, startDate: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Дата по
                </label>
                <input
                  type="date"
                  value={filter.endDate}
                  onChange={(e) => setFilter({...filter, endDate: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setFilter({fromAccountId: '', toAccountId: '', startDate: '', endDate: ''})}
                  className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Сбросить
                </button>
              </div>
            </div>
          </div>

          {/* Список операций */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Дата
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Описание
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Сумма
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Откуда
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Куда
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {cashFlows.map((flow) => (
                    <tr key={flow.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(flow.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {flow.description}
                        </div>
                        {flow.tournament && (
                          <div className="text-sm text-gray-500">
                            {flow.tournament.name}
                          </div>
                        )}
                        {flow.reference && (
                          <div className="text-sm text-gray-400">
                            Ссылка: {flow.reference}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatAmount(flow.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {flow.fromAccount.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {flow.toAccount.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleEdit(flow)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="Редактировать"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button 
                            onClick={() => handleDelete(flow.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Удалить"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          <button 
                            onClick={() => handleViewJSON(flow)}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                            title="Просмотр JSON"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {cashFlows.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">Операции не найдены</p>
              </div>
            )}
          </div>

      {showForm && (
        <CashFlowForm
          cashFlow={editingCashFlow || undefined}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}

      {showJSONModal && selectedCashFlow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                JSON View - Операция {selectedCashFlow.id}
              </h2>
              <button
                onClick={handleCloseJSONModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-gray-100 rounded-lg p-4 overflow-auto max-h-[70vh]">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(selectedCashFlow, null, 2)}
              </pre>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleCloseJSONModal}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashFlow;
